import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { PropertyKitEntry, UpdateEntryInput, PropertyKitItem } from './types';

const QUERY_KEY = ['property-articles'];

export function usePropertyArticles(propertyId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch entries for a property
  const query = useQuery({
    queryKey: [...QUERY_KEY, propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<PropertyKitEntry[]> => {
      const { data, error } = await supabase
        .from('property_kit_entries')
        .select(`
          *,
          item:property_kit_items(
            *,
            category:property_kit_categories(*)
          )
        `)
        .eq('property_id', propertyId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as unknown as PropertyKitEntry[];
    },
  });

  // Add item to property
  const addEntryMutation = useMutation({
    mutationFn: async ({ itemId, item }: { itemId: string; item: PropertyKitItem }) => {
      // Set initial values based on control type
      let asset_status: 'present' | 'missing' | 'damaged' | null = null;
      let consumable_status: 'full' | 'half' | 'low' | 'empty' | null = null;
      let current_quantity: number | null = null;
      let ideal_quantity: number | null = null;

      switch (item.control_type) {
        case 'fixed_asset':
          asset_status = 'present';
          break;
        case 'consumable':
          consumable_status = 'full';
          break;
        case 'countable':
          // Copy ideal_quantity from catalog as initial value for property
          ideal_quantity = item.ideal_quantity ?? null;
          current_quantity = item.ideal_quantity ?? 0;
          break;
      }

      const { data, error } = await supabase
        .from('property_kit_entries')
        .insert({
          property_id: propertyId!,
          item_id: itemId,
          asset_status,
          consumable_status,
          current_quantity,
          ideal_quantity,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property-articles-count'] });
      toast({
        title: 'Artículo agregado',
        description: 'El artículo se ha agregado a la propiedad.',
      });
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate') || error.message.includes('unique')) {
        toast({
          title: 'Error',
          description: 'Este artículo ya está en la propiedad.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: `No se pudo agregar el artículo: ${error.message}`,
          variant: 'destructive',
        });
      }
    },
  });

  // Update entry (status or quantity)
  const updateEntryMutation = useMutation({
    mutationFn: async ({ entryId, ...input }: UpdateEntryInput & { entryId: string }) => {
      // Get current entry to record history
      const { data: currentEntry, error: fetchError } = await supabase
        .from('property_kit_entries')
        .select('*')
        .eq('id', entryId)
        .single();

      if (fetchError) throw fetchError;

      // Update entry
      const { data, error } = await supabase
        .from('property_kit_entries')
        .update({
          current_quantity: input.current_quantity,
          ideal_quantity: input.ideal_quantity,
          asset_status: input.asset_status,
          consumable_status: input.consumable_status,
        })
        .eq('id', entryId)
        .select()
        .single();

      if (error) throw error;

      // Record history
      const { data: { user } } = await supabase.auth.getUser();
      
      const hasQuantityChange = input.current_quantity !== undefined || input.ideal_quantity !== undefined;
      const changeType = hasQuantityChange ? 'quantity_change' : 'status_change';
      const previousValue: { [key: string]: string | number | null } = {};
      const newValue: { [key: string]: string | number | null } = {};

      if (input.current_quantity !== undefined) {
        previousValue.current_quantity = currentEntry.current_quantity;
        newValue.current_quantity = input.current_quantity;
      }
      if (input.ideal_quantity !== undefined) {
        previousValue.ideal_quantity = currentEntry.ideal_quantity;
        newValue.ideal_quantity = input.ideal_quantity;
      }
      if (input.asset_status !== undefined) {
        previousValue.asset_status = currentEntry.asset_status;
        newValue.asset_status = input.asset_status;
      }
      if (input.consumable_status !== undefined) {
        previousValue.consumable_status = currentEntry.consumable_status;
        newValue.consumable_status = input.consumable_status;
      }

      if (user?.id) {
        await supabase.from('property_kit_history').insert([{
          entry_id: entryId,
          changed_by: user.id,
          previous_value: previousValue as unknown as Record<string, never>,
          new_value: newValue as unknown as Record<string, never>,
          change_type: changeType,
        }]);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property-articles-count'] });
      toast({
        title: 'Actualizado',
        description: 'El estado se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Remove entry from property
  const removeEntryMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase
        .from('property_kit_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property-articles-count'] });
      toast({
        title: 'Artículo eliminado',
        description: 'El artículo se ha eliminado de la propiedad.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo eliminar el artículo: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Add multiple items from template (only if property is empty)
  const addFromTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      // Verify property has no articles
      const { count, error: countError } = await supabase
        .from('property_kit_entries')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', propertyId);

      if (countError) throw countError;
      
      if (count && count > 0) {
        throw new Error('La propiedad ya tiene artículos. Solo se puede aplicar plantilla a propiedades vacías.');
      }

      // Get template items
      const { data: templateItems, error: templateError } = await supabase
        .from('property_kit_template_items')
        .select(`
          *,
          item:property_kit_items(*)
        `)
        .eq('template_id', templateId);

      if (templateError) throw templateError;

      // Create entries for each template item
      const entries: Array<{
        property_id: string;
        item_id: string;
        asset_status: 'present' | 'missing' | 'damaged' | null;
        consumable_status: 'full' | 'half' | 'low' | 'empty' | null;
        current_quantity: number | null;
        ideal_quantity: number | null;
      }> = [];

      for (const ti of templateItems) {
        const item = ti.item as unknown as PropertyKitItem;
        let asset_status: 'present' | 'missing' | 'damaged' | null = null;
        let consumable_status: 'full' | 'half' | 'low' | 'empty' | null = null;
        let current_quantity: number | null = null;
        let ideal_quantity: number | null = null;

        switch (item.control_type) {
          case 'fixed_asset':
            asset_status = 'present';
            break;
          case 'consumable':
            consumable_status = 'full';
            break;
          case 'countable':
            // Use template's default_quantity as ideal, or fall back to catalog's ideal
            ideal_quantity = ti.default_quantity ?? item.ideal_quantity ?? null;
            current_quantity = ideal_quantity ?? 0;
            break;
        }

        entries.push({
          property_id: propertyId!,
          item_id: ti.item_id,
          asset_status,
          consumable_status,
          current_quantity,
          ideal_quantity,
        });
      }

      if (entries.length > 0) {
        const { error } = await supabase
          .from('property_kit_entries')
          .insert(entries);

        if (error) throw error;
      }

      return entries.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property-articles-count'] });
      toast({
        title: 'Plantilla aplicada',
        description: `Se agregaron ${count} artículos a la propiedad.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    entries: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    addEntry: addEntryMutation.mutateAsync,
    updateEntry: updateEntryMutation.mutateAsync,
    removeEntry: removeEntryMutation.mutateAsync,
    addFromTemplate: addFromTemplateMutation.mutateAsync,
    isAdding: addEntryMutation.isPending,
    isUpdating: updateEntryMutation.isPending,
    isRemoving: removeEntryMutation.isPending,
    isApplyingTemplate: addFromTemplateMutation.isPending,
  };
}
